import { useEffect, useState } from 'react';
import { SanityHotspotImage } from '@portal/schema/studio/common/hotspotImage';

export const getImageCrop = (
  { hotspot }: SanityHotspotImage,
  {
    width,
    height
  }: {
    width: number;
    height: number;
  }
) => {
  if (hotspot && width && height) {
    return {
      width,
      height,
      hotspotWidth: hotspot.width,
      hotspotHeight: hotspot.height,
      hotspotX: hotspot.x,
      hotspotY: hotspot.y
    };
  }

  return null;
};

type ImageCropResult = ReturnType<typeof getImageCrop>;

const observerCallback = (imageCrop: ImageCropResult | null, entries: ResizeObserverEntry[]) => {
  if (!imageCrop) {
    return;
  }

  const entry = entries[entries.length - 1];
  const picture = entry.target as HTMLPictureElement;

  let pictureAspectRatio: number;

  try {
    if (entry.contentBoxSize) {
      pictureAspectRatio = entry.contentBoxSize[0].inlineSize / entry.contentBoxSize[0].blockSize;
    } else {
      pictureAspectRatio = entry.contentRect.width / entry.contentRect.height;
    }
  } catch (_err) {
    pictureAspectRatio = 1;
  }

  const {
    width: imageWidth,
    height: imageHeight,
    hotspotWidth: cropWidth,
    hotspotHeight: cropHeight
  } = imageCrop;

  const imageAspectRatio = imageWidth / imageHeight;
  const cropAspectRatio = (imageAspectRatio * cropWidth) / cropHeight;

  const scale =
    pictureAspectRatio > cropAspectRatio
      ? imageAspectRatio / pictureAspectRatio
      : imageAspectRatio / cropAspectRatio;

  picture.style.setProperty('--scale-factor', Math.max(scale, 1).toFixed(3));
};

export const useImageScale = (imageCrop: ImageCropResult | null) => {
  const [nodeRef, setNodeRef] = useState<HTMLPictureElement | null>(null);
  const [observer] = useState<ResizeObserver | null>(() => {
    if (typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') {
      return null;
    }

    const boundCallback = observerCallback.bind(null, imageCrop);

    return new ResizeObserver(boundCallback);
  });

  useEffect(() => {
    if (nodeRef && observer) {
      observer.observe(nodeRef);
    }

    return () => {
      if (nodeRef && observer) {
        observer.disconnect();
      }
    };
  }, [nodeRef, observer]);

  return setNodeRef;
};
